import type { InputState } from '../types';

export type PowerupType = 'spread' | 'rapid' | 'shield';

export interface PlayerState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  angle: number;
  radius: number;
  lives: number;
  maxLives: number;
  invulnTimer: number;
  fireCooldown: number;
  spreadTimer: number;
  rapidTimer: number;
}

export interface AsteroidEntity {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  tier: number; // 3: large, 2: medium, 1: small
  angle: number;
  rotSpeed: number;
  isTrapped: boolean;
  trappedTimer: number;
  bubbleRadius: number;
}

export interface InvaderEntity {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  row: number;
  col: number;
  isTrapped: boolean;
  trappedTimer: number;
  bubbleRadius: number;
}

export interface BulletEntity {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  isEnemy: boolean;
}

export interface PowerupEntity {
  id: number;
  x: number;
  y: number;
  vy: number;
  type: PowerupType;
  radius: number;
}

export interface BarricadeBlock {
  id: number;
  x: number;
  y: number;
  width: number;
  height: number;
  hp: number;
  maxHp: number;
}

export interface ParticleEntity {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
}

export interface MusicBandsInput {
  energy: number;
  kick: number;
  beat: number;
}

/**
 * Headless, DOM-independent core game engine for ASTRO-BUBBLE DEFENDER.
 * Handles state, physics, update loops, spatial collision detection, wave progression,
 * and score calculation for easy unit testing.
 */
export class CyberDefenderEngine {
  readonly width = 800;
  readonly height = 600;

  player: PlayerState;
  asteroids: AsteroidEntity[] = [];
  invaders: InvaderEntity[] = [];
  bullets: BulletEntity[] = [];
  powerups: PowerupEntity[] = [];
  barricades: BarricadeBlock[] = [];
  particles: ParticleEntity[] = [];

  score = 0;
  wave = 1;
  state: 'playing' | 'gameover' = 'playing';
  message: string | undefined = 'DEFEND THE GRID! FIRE TO SHOOT / TRAP';
  messageTimer = 3.0;

  invaderMarchX = 0;
  invaderMarchDir = 1;
  invaderMarchCd = 0;

  private nextEntityId = 1;

  constructor() {
    this.player = this.createDefaultPlayer();
    this.initGame();
  }

  private createDefaultPlayer(): PlayerState {
    return {
      x: this.width / 2,
      y: this.height - 70,
      vx: 0,
      vy: 0,
      angle: -Math.PI / 2, // facing upward
      radius: 14,
      lives: 3,
      maxLives: 5,
      invulnTimer: 0,
      fireCooldown: 0,
      spreadTimer: 0,
      rapidTimer: 0,
    };
  }

  initGame(): void {
    this.score = 0;
    this.wave = 1;
    this.state = 'playing';
    this.message = 'DEFEND THE GRID! FIRE TO SHOOT / TRAP';
    this.messageTimer = 3.0;
    this.player = this.createDefaultPlayer();
    this.asteroids = [];
    this.invaders = [];
    this.bullets = [];
    this.powerups = [];
    this.particles = [];
    this.initBarricades();
    this.spawnWave(1);
  }

  private initBarricades(): void {
    this.barricades = [];
    const count = 4;
    const spacing = this.width / (count + 1);
    const barricadeY = this.height - 130;

    for (let i = 0; i < count; i++) {
      const centerX = spacing * (i + 1);
      // 3x2 block grid per barricade shield
      for (let bx = -1; bx <= 1; bx++) {
        for (let by = -1; by <= 0; by++) {
          this.barricades.push({
            id: this.nextEntityId++,
            x: centerX + bx * 18,
            y: barricadeY + by * 14,
            width: 16,
            height: 12,
            hp: 8,
            maxHp: 8,
          });
        }
      }
    }
  }

  spawnWave(waveNum: number): void {
    this.wave = waveNum;
    this.asteroids = [];
    this.invaders = [];
    this.bullets = [];
    this.invaderMarchX = 0;
    this.invaderMarchDir = 1;

    // Spawn Space Invader formation grid
    const cols = Math.min(8, 4 + Math.floor(waveNum / 2));
    const rows = Math.min(4, 2 + Math.floor(waveNum / 3));
    const startX = this.width / 2 - ((cols - 1) * 45) / 2;
    const startY = 80;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        this.invaders.push({
          id: this.nextEntityId++,
          x: startX + c * 45,
          y: startY + r * 35,
          vx: 0,
          vy: 0,
          radius: 12,
          row: r,
          col: c,
          isTrapped: false,
          trappedTimer: 0,
          bubbleRadius: 18,
        });
      }
    }

    // Spawn floating Asteroids
    const asteroidCount = 2 + Math.min(6, waveNum);
    for (let i = 0; i < asteroidCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 30 + Math.random() * 40 + waveNum * 5;
      const posX = 50 + Math.random() * (this.width - 100);
      const posY = 60 + Math.random() * 120;

      this.asteroids.push({
        id: this.nextEntityId++,
        x: posX,
        y: posY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: 22,
        tier: 3,
        angle: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 2,
        isTrapped: false,
        trappedTimer: 0,
        bubbleRadius: 28,
      });
    }

    this.message = `WAVE ${waveNum} INCOMING!`;
    this.messageTimer = 2.0;
  }

  update(dt: number, input: InputState, musicBands?: MusicBandsInput): void {
    if (this.messageTimer > 0) {
      this.messageTimer -= dt;
      if (this.messageTimer <= 0) {
        this.message = undefined;
      }
    }

    if (this.state === 'gameover') {
      if (input.firePressed) {
        this.initGame();
      }
      return;
    }

    const energy = musicBands?.energy ?? 0.2;
    const kick = musicBands?.kick ?? 0;

    this.updatePlayer(dt, input);
    this.updateBullets(dt);
    this.updateAsteroids(dt);
    this.updateInvaders(dt, energy, kick);
    this.updatePowerups(dt);
    this.updateParticles(dt);
    this.checkCollisions();

    // Check wave clearing
    if (this.asteroids.length === 0 && this.invaders.length === 0 && this.player.lives > 0) {
      this.score += 250 * this.wave;
      this.spawnWave(this.wave + 1);
    }
  }

  private updatePlayer(dt: number, input: InputState): void {
    const p = this.player;

    if (p.invulnTimer > 0) p.invulnTimer -= dt;
    if (p.fireCooldown > 0) p.fireCooldown -= dt;
    if (p.spreadTimer > 0) p.spreadTimer -= dt;
    if (p.rapidTimer > 0) p.rapidTimer -= dt;

    // Rotation
    const rotSpeed = 3.5;
    if (input.left) p.angle -= rotSpeed * dt;
    if (input.right) p.angle += rotSpeed * dt;

    // Direct movement / Thrust
    const accel = 450;
    if (input.up) {
      p.vx += Math.cos(p.angle) * accel * dt;
      p.vy += Math.sin(p.angle) * accel * dt;
    }
    if (input.down) {
      p.vx -= Math.cos(p.angle) * (accel * 0.5) * dt;
      p.vy -= Math.sin(p.angle) * (accel * 0.5) * dt;
    }

    // Apply friction & update position
    p.vx *= Math.pow(0.88, dt * 60);
    p.vy *= Math.pow(0.88, dt * 60);
    p.x += p.vx * dt;
    p.y += p.vy * dt;

    // Screen bounds clamp with soft restitution
    const margin = p.radius + 10;
    if (p.x < margin) {
      p.x = margin;
      p.vx = Math.abs(p.vx) * 0.5;
    } else if (p.x > this.width - margin) {
      p.x = this.width - margin;
      p.vx = -Math.abs(p.vx) * 0.5;
    }
    if (p.y < margin + 40) {
      p.y = margin + 40;
      p.vy = Math.abs(p.vy) * 0.5;
    } else if (p.y > this.height - margin) {
      p.y = this.height - margin;
      p.vy = -Math.abs(p.vy) * 0.5;
    }

    // Shooting
    if ((input.firePressed || (input.fire && p.fireCooldown <= 0)) && p.fireCooldown <= 0) {
      this.fireHarpoon();
      const baseCd = p.rapidTimer > 0 ? 0.12 : 0.25;
      p.fireCooldown = baseCd;
    }
  }

  private fireHarpoon(): void {
    const p = this.player;
    const speed = 550;
    const noseX = p.x + Math.cos(p.angle) * p.radius;
    const noseY = p.y + Math.sin(p.angle) * p.radius;

    // Primary bullet
    this.bullets.push({
      id: this.nextEntityId++,
      x: noseX,
      y: noseY,
      vx: Math.cos(p.angle) * speed,
      vy: Math.sin(p.angle) * speed,
      radius: 4,
      isEnemy: false,
    });

    // Spread powerup shots
    if (p.spreadTimer > 0) {
      const spreadAngle = 0.28;
      this.bullets.push({
        id: this.nextEntityId++,
        x: noseX,
        y: noseY,
        vx: Math.cos(p.angle - spreadAngle) * speed,
        vy: Math.sin(p.angle - spreadAngle) * speed,
        radius: 4,
        isEnemy: false,
      });
      this.bullets.push({
        id: this.nextEntityId++,
        x: noseX,
        y: noseY,
        vx: Math.cos(p.angle + spreadAngle) * speed,
        vy: Math.sin(p.angle + spreadAngle) * speed,
        radius: 4,
        isEnemy: false,
      });
    }
  }

  private updateBullets(dt: number): void {
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i];
      b.x += b.vx * dt;
      b.y += b.vy * dt;

      // Offscreen removal
      if (b.x < -20 || b.x > this.width + 20 || b.y < -20 || b.y > this.height + 20) {
        this.bullets.splice(i, 1);
      }
    }
  }

  private updateAsteroids(dt: number): void {
    for (const a of this.asteroids) {
      if (a.isTrapped) {
        a.trappedTimer -= dt;
        // Float upward due to bubble buoyancy
        a.vy = -35;
        a.vx *= 0.95;
        a.y += a.vy * dt;
        a.x += a.vx * dt;

        if (a.trappedTimer <= 0) {
          a.isTrapped = false; // escapes bubble!
        }
      } else {
        a.x += a.vx * dt;
        a.y += a.vy * dt;
        a.angle += a.rotSpeed * dt;

        // Screen wrap
        if (a.x < -a.radius) a.x = this.width + a.radius;
        if (a.x > this.width + a.radius) a.x = -a.radius;
        if (a.y < -a.radius) a.y = this.height + a.radius;
        if (a.y > this.height + a.radius) a.y = -a.radius;
      }
    }
  }

  private updateInvaders(dt: number, energy: number, kick: number): void {
    this.invaderMarchCd -= dt;

    // March step timing
    const stepInterval = Math.max(0.15, 0.6 - this.wave * 0.03 - energy * 0.25);
    if (this.invaderMarchCd <= 0 && this.invaders.length > 0) {
      this.invaderMarchCd = stepInterval;
      const stepDist = 12 + energy * 6;
      this.invaderMarchX += this.invaderMarchDir * stepDist;

      // Check bounds to reverse and drop
      let minX = Infinity;
      let maxX = -Infinity;
      for (const inv of this.invaders) {
        if (!inv.isTrapped) {
          minX = Math.min(minX, inv.x);
          maxX = Math.max(maxX, inv.x);
        }
      }

      if (maxX > this.width - 40 && this.invaderMarchDir > 0) {
        this.invaderMarchDir = -1;
        this.dropInvaders(15 + kick * 5);
      } else if (minX < 40 && this.invaderMarchDir < 0) {
        this.invaderMarchDir = 1;
        this.dropInvaders(15 + kick * 5);
      } else {
        for (const inv of this.invaders) {
          if (!inv.isTrapped) {
            inv.x += this.invaderMarchDir * stepDist;
          }
        }
      }

      // Random enemy shooting
      if (Math.random() < 0.2 + energy * 0.3) {
        const activeInvaders = this.invaders.filter((inv) => !inv.isTrapped);
        if (activeInvaders.length > 0) {
          const shooter = activeInvaders[Math.floor(Math.random() * activeInvaders.length)];
          this.bullets.push({
            id: this.nextEntityId++,
            x: shooter.x,
            y: shooter.y + 10,
            vx: (Math.random() - 0.5) * 40,
            vy: 200 + this.wave * 20,
            radius: 4,
            isEnemy: true,
          });
        }
      }
    }

    // Update trapped invaders
    for (const inv of this.invaders) {
      if (inv.isTrapped) {
        inv.trappedTimer -= dt;
        inv.vy = -35;
        inv.y += inv.vy * dt;
        if (inv.trappedTimer <= 0) {
          inv.isTrapped = false;
        }
      }
    }
  }

  private dropInvaders(dist: number): void {
    for (const inv of this.invaders) {
      if (!inv.isTrapped) {
        inv.y += dist;
      }
    }
  }

  private updatePowerups(dt: number): void {
    for (let i = this.powerups.length - 1; i >= 0; i--) {
      const p = this.powerups[i];
      p.y += p.vy * dt;
      if (p.y > this.height + 20) {
        this.powerups.splice(i, 1);
      }
    }
  }

  private updateParticles(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const pt = this.particles[i];
      pt.life -= dt;
      pt.x += pt.vx * dt;
      pt.y += pt.vy * dt;
      if (pt.life <= 0) {
        this.particles.splice(i, 1);
      }
    }
  }

  private spawnExplosion(x: number, y: number, color: string, count = 12): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 40 + Math.random() * 160;
      this.particles.push({
        id: this.nextEntityId++,
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.3 + Math.random() * 0.4,
        maxLife: 0.7,
        color,
        size: 2 + Math.random() * 4,
      });
    }
  }

  private triggerChainDetonation(startX: number, startY: number): number {
    let chainCount = 0;
    const queue: { x: number; y: number }[] = [{ x: startX, y: startY }];
    const blastRadius = 80;

    while (queue.length > 0) {
      const curr = queue.shift()!;

      // Pop asteroids
      for (let i = this.asteroids.length - 1; i >= 0; i--) {
        const ast = this.asteroids[i];
        if (ast.isTrapped && Math.hypot(ast.x - curr.x, ast.y - curr.y) <= blastRadius) {
          chainCount++;
          this.spawnExplosion(ast.x, ast.y, '#00ffcc', 14);
          this.maybeSpawnPowerup(ast.x, ast.y);

          // Split into smaller tier asteroids if tier > 1
          if (ast.tier > 1) {
            for (let k = 0; k < 2; k++) {
              const angle = Math.random() * Math.PI * 2;
              const spd = 60 + Math.random() * 50;
              this.asteroids.push({
                id: this.nextEntityId++,
                x: ast.x,
                y: ast.y,
                vx: Math.cos(angle) * spd,
                vy: Math.sin(angle) * spd,
                radius: ast.tier === 3 ? 15 : 9,
                tier: ast.tier - 1,
                angle: Math.random() * Math.PI * 2,
                rotSpeed: (Math.random() - 0.5) * 3,
                isTrapped: false,
                trappedTimer: 0,
                bubbleRadius: ast.tier === 3 ? 20 : 12,
              });
            }
          }

          queue.push({ x: ast.x, y: ast.y });
          this.asteroids.splice(i, 1);
        }
      }

      // Pop invaders
      for (let i = this.invaders.length - 1; i >= 0; i--) {
        const inv = this.invaders[i];
        if (inv.isTrapped && Math.hypot(inv.x - curr.x, inv.y - curr.y) <= blastRadius) {
          chainCount++;
          this.spawnExplosion(inv.x, inv.y, '#ff00aa', 14);
          this.maybeSpawnPowerup(inv.x, inv.y);
          queue.push({ x: inv.x, y: inv.y });
          this.invaders.splice(i, 1);
        }
      }
    }

    if (chainCount > 0) {
      const points = Math.pow(2, Math.min(6, chainCount)) * 100;
      this.score += points;
      this.message = `CHAIN COMBO x${chainCount}! +${points}`;
      this.messageTimer = 1.5;
    }

    return chainCount;
  }

  private maybeSpawnPowerup(x: number, y: number): void {
    if (Math.random() < 0.25) {
      const types: PowerupType[] = ['spread', 'rapid', 'shield'];
      const type = types[Math.floor(Math.random() * types.length)];
      this.powerups.push({
        id: this.nextEntityId++,
        x,
        y,
        vy: 60,
        type,
        radius: 10,
      });
    }
  }

  private checkCollisions(): void {
    // 1. Bullets vs Entities / Barricades
    for (let bi = this.bullets.length - 1; bi >= 0; bi--) {
      const b = this.bullets[bi];

      if (!b.isEnemy) {
        // Player Harpoon vs Asteroids
        let hit = false;
        for (const ast of this.asteroids) {
          const dist = Math.hypot(b.x - ast.x, b.y - ast.y);
          if (dist < b.radius + (ast.isTrapped ? ast.bubbleRadius : ast.radius)) {
            hit = true;
            if (!ast.isTrapped) {
              // Encase in bubble!
              ast.isTrapped = true;
              ast.trappedTimer = 8.0;
              this.score += 20;
            } else {
              // Pop bubble and trigger chain reaction
              this.triggerChainDetonation(ast.x, ast.y);
            }
            break;
          }
        }

        if (hit) {
          this.bullets.splice(bi, 1);
          continue;
        }

        // Player Harpoon vs Invaders
        for (const inv of this.invaders) {
          const dist = Math.hypot(b.x - inv.x, b.y - inv.y);
          if (dist < b.radius + (inv.isTrapped ? inv.bubbleRadius : inv.radius)) {
            hit = true;
            if (!inv.isTrapped) {
              inv.isTrapped = true;
              inv.trappedTimer = 8.0;
              this.score += 30;
            } else {
              this.triggerChainDetonation(inv.x, inv.y);
            }
            break;
          }
        }

        if (hit) {
          this.bullets.splice(bi, 1);
          continue;
        }
      } else {
        // Enemy Laser vs Player
        if (this.player.invulnTimer <= 0) {
          const dist = Math.hypot(b.x - this.player.x, b.y - this.player.y);
          if (dist < b.radius + this.player.radius) {
            this.damagePlayer();
            this.bullets.splice(bi, 1);
            continue;
          }
        }
      }

      // Bullets vs Barricades
      for (let barI = this.barricades.length - 1; barI >= 0; barI--) {
        const bar = this.barricades[barI];
        if (
          b.x >= bar.x - bar.width / 2 &&
          b.x <= bar.x + bar.width / 2 &&
          b.y >= bar.y - bar.height / 2 &&
          b.y <= bar.y + bar.height / 2
        ) {
          bar.hp -= 1;
          this.spawnExplosion(b.x, b.y, '#88aaff', 4);
          if (bar.hp <= 0) {
            this.barricades.splice(barI, 1);
          }
          this.bullets.splice(bi, 1);
          break;
        }
      }
    }

    // 2. Untrapped Hazards vs Player
    if (this.player.invulnTimer <= 0) {
      for (const ast of this.asteroids) {
        if (!ast.isTrapped && Math.hypot(ast.x - this.player.x, ast.y - this.player.y) < ast.radius + this.player.radius) {
          this.damagePlayer();
          break;
        }
      }
      for (const inv of this.invaders) {
        if (!inv.isTrapped && Math.hypot(inv.x - this.player.x, inv.y - this.player.y) < inv.radius + this.player.radius) {
          this.damagePlayer();
          break;
        }
      }
    }

    // 3. Player vs Powerup items
    for (let i = this.powerups.length - 1; i >= 0; i--) {
      const p = this.powerups[i];
      if (Math.hypot(p.x - this.player.x, p.y - this.player.y) < p.radius + this.player.radius) {
        this.applyPowerup(p.type);
        this.powerups.splice(i, 1);
      }
    }
  }

  private applyPowerup(type: PowerupType): void {
    if (type === 'spread') {
      this.player.spreadTimer += 8.0;
      this.message = 'POWERUP: TRIPLE SPREAD!';
    } else if (type === 'rapid') {
      this.player.rapidTimer += 8.0;
      this.message = 'POWERUP: RAPID FIRE!';
    } else if (type === 'shield') {
      this.player.lives = Math.min(this.player.maxLives, this.player.lives + 1);
      this.message = 'POWERUP: SHIELD RESTORED!';
    }
    this.messageTimer = 2.0;
    this.score += 100;
  }

  private damagePlayer(): void {
    this.player.lives -= 1;
    this.player.invulnTimer = 2.0;
    this.spawnExplosion(this.player.x, this.player.y, '#ff4444', 20);

    if (this.player.lives <= 0) {
      this.state = 'gameover';
      this.message = 'GAME OVER! PRESS FIRE TO RESTART';
      this.messageTimer = 999;
    } else {
      this.message = 'SHIELD BREACHED!';
      this.messageTimer = 1.5;
    }
  }
}
